import { useMutation, useQuery } from '@apollo/client';
import { OptionalString, parseQueryString } from '@sixfold/app-utils';
import { isNil } from '@sixfold/typed-primitives';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { recordRecentTour } from '../../home/storage';
import {
  AbortTourMutation,
  AbortTourMutationVariables,
  notEmpty,
  RecalculateTourMutation,
  RecalculateTourMutationVariables,
  TourQuery,
  TourQueryVariables,
  TourVehicleHistoryQuery,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import {
  HISTORY_SIMPLIFICATION_QS,
  TIMELINE_END_TIME_QS,
  TIMELINE_START_TIME_QS,
} from '../../map/components/map_slider';
import { mergeTourVehiclesWithVehiclesHistory } from '../../vehicle/utils';
import { HighlightedStopIdProvider } from '../components/stop_highlight_context';
import { TourDetailsView } from '../components/tour_details';
import { Tour } from '../entities';
import { abortTourMutation, recalculateTourMutation, tourQuery, vehicleHistoryQuery } from '../graphql';
import { extractVehicleHistoryVariables, transformCombinedEventsToEvents } from '../utils';
import { TourMapContainer } from './tour_map_container';
import { useTourTagMutations } from './tour_tag_container';

class LoadingContainer extends Loader<TourQuery, Tour | null, TourQueryVariables> {}

function mapTourData(data: TourQuery): Tour | null {
  const { tour, availableTourTags } = data;
  if (tour === null || tour === undefined) {
    return null;
  }
  return {
    ...tour,
    availableTourTags,
    events: transformCombinedEventsToEvents(tour.events),
    stops: (tour.stops ?? []).filter(notEmpty).map((stop) => ({
      ...stop,
      status_events: (stop.status_events ?? []).filter(notEmpty),
      geofenceZones: stop.geofenceZones,
    })),
    route:
      tour.route !== null
        ? {
            provider: tour.route.provider,
            legs: (tour.route.legs ?? []).filter(notEmpty).filter((leg) => leg.polyline !== null),
          }
        : null,
    status_events: (tour.status_events ?? []).filter(notEmpty),
  } as Tour;
}

export const TourContainer = () => {
  const { tour_id } = useParams() as { tour_id: string };
  const location = useLocation();
  const { history_simplification_tolerance, timeline_start, timeline_end } = parseQueryString({
    queryParametersToParse: {
      [HISTORY_SIMPLIFICATION_QS]: OptionalString,
      [TIMELINE_START_TIME_QS]: OptionalString,
      [TIMELINE_END_TIME_QS]: OptionalString,
    },
    queryString: location.search,
  });

  const [tourSidebarActive, setTourSidebarActive] = useState<boolean>(true);

  const result = useQuery<TourQuery, TourQueryVariables>(tourQuery, {
    variables: { tour_id: parseInt(tour_id, 10) },
  });

  const [abortTour] = useMutation<AbortTourMutation, AbortTourMutationVariables>(abortTourMutation);
  const [recalculateTour] = useMutation<RecalculateTourMutation, RecalculateTourMutationVariables>(
    recalculateTourMutation,
  );

  const { addTourTag, removeTourTag } = useTourTagMutations();

  const mappedTour = useMemo(() => {
    if (!result.data) return null;
    return mapTourData(result.data);
  }, [result.data]);

  useEffect(() => {
    if (isNil(mappedTour)) {
      return;
    }

    let fromToStops;
    if (mappedTour.stops.length > 0) {
      const first = mappedTour.stops[0];
      const last = mappedTour.stops[mappedTour.stops.length - 1];
      const fromName = first.location?.name ?? `Stop ${first.display_sequence_number}`;
      const toName = last.location?.name ?? `Stop ${last.display_sequence_number}`;
      fromToStops = `${fromName} - ${toName}`;
    }

    recordRecentTour({
      tourId: mappedTour.tour_id,
      transportId: mappedTour.transport_id,
      shipperTransportNumber: mappedTour.shipper_transport_number ?? undefined,
      fromTo: fromToStops,
      shipperName: mappedTour.shipper?.company_name ?? undefined,
      carrierName: mappedTour.carrier?.company_name ?? undefined,
      createdAt: mappedTour.created_at,
    });
  }, [mappedTour]);

  const vehicleHistoryQueryVariables = useMemo(() => {
    if (!mappedTour) return null;
    if (timeline_start !== undefined && timeline_end !== undefined) {
      return {
        tour_id: mappedTour.tour_id,
        start_time: new Date(timeline_start),
        end_time: new Date(timeline_end),
      };
    }
    return extractVehicleHistoryVariables(mappedTour);
  }, [mappedTour, timeline_start, timeline_end]);

  const historyResult = useQuery<TourVehicleHistoryQuery>(vehicleHistoryQuery, {
    skip: vehicleHistoryQueryVariables === null,
    variables: {
      ...(vehicleHistoryQueryVariables ?? {}),
      simplification_tolerance:
        history_simplification_tolerance !== undefined ? parseFloat(history_simplification_tolerance) : undefined,
    },
  });

  return (
    <HighlightedStopIdProvider>
      <LoadingContainer result={result} mapData={({ data }) => mapTourData(data as TourQuery)}>
        {(tour) => {
          if (tour === null) {
            return <span>No tour to display</span>;
          }

          const tourActions = {
            onTourAbort: (tourId: number) => abortTour({ variables: { tour_id: tourId } }),
            onTourRecalculate: (tourId: number) => recalculateTour({ variables: { tour_id: tourId } }),
            onAddTourTag: () => false,
            onRemoveTourTag: () => false,
          };

          const vehicles = mergeTourVehiclesWithVehiclesHistory(
            historyResult.data,
            tour.vehicleAllocationHistory,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            vehicleHistoryQueryVariables!.end_time,
          );

          return (
            <div className="tour">
              {tourSidebarActive && (
                <TourDetailsView
                  className="tour__details-column"
                  data={{
                    tour,
                    availableTourTags: tour.availableTourTags,
                  }}
                  {...tourActions}
                  onAddTourTag={(options) =>
                    addTourTag({ variables: { tour_id: options.tourId, tag_name: options.tagName } })
                  }
                  onRemoveTourTag={(options) =>
                    removeTourTag({ variables: { tour_id: options.tourId, tag_name: options.tagName } })
                  }
                />
              )}
              <TourMapContainer
                tour={tour}
                vehicles={vehicles}
                tourSidebarState={{
                  tourSidebarActive,
                  setTourSidebarActive,
                }}
                historyFetchingBoundaries={{
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  startTime: vehicleHistoryQueryVariables!.start_time.toISOString(),
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  endTime: vehicleHistoryQueryVariables!.end_time.toISOString(),
                }}
              />
            </div>
          );
        }}
      </LoadingContainer>
    </HighlightedStopIdProvider>
  );
};
